import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ClipboardList, Upload, 
  Settings, LogOut, Menu, X, Globe, FileText, MessageSquare,
  Image as ImageIcon, Mail, BarChart3, Send, Target, Truck,
  ShoppingCart, ClipboardCheck, Users, UserCheck, Search as SearchIcon, Bell, ChevronRight
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getAdminStats } from '../../lib/api';
import { AdminSearchBar } from '../../components/AdminSearchBar';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_9e7963b6-8d0a-418d-8246-af7a5b4f8387/artifacts/h6dgewwm_image.png';

export const AdminLayout = () => {
  const { adminInfo, logout, isAdmin, t, language, setLanguage } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({ pending_review: 0, pending_orders: 0, pending_leads: 0 });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/login');
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await getAdminStats();
        setStats(res.data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };
    if (isAdmin) {
      fetchStats();
      const interval = setInterval(fetchStats, 30000);
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navSections = [
    {
      title: 'Dashboard',
      items: [
        { path: '/admin', icon: LayoutDashboard, label: t('adminNav.dashboard'), exact: true, testid: 'nav-dashboard' },
      ],
    },
    {
      title: 'Catalog',
      items: [
        { path: '/admin/products', icon: Package, label: t('adminNav.products'), testid: 'nav-products' },
        { path: '/admin/bulk-lots', icon: Package, label: 'Bulk Lots', testid: 'nav-bulk-lots' },
        { path: '/admin/gallery', icon: ImageIcon, label: 'Gallery', testid: 'nav-gallery' },
        { path: '/admin/import', icon: Upload, label: t('adminNav.import'), badge: stats.pending_review, testid: 'nav-import' },
      ],
    },
    {
      title: 'Sales',
      items: [
        { path: '/admin/customers', icon: Users, label: 'Customers', testid: 'nav-customers' },
        { path: '/admin/quotes', icon: FileText, label: 'Quotes', badge: stats.pending_quotes, testid: 'nav-quotes' },
        { path: '/admin/orders', icon: ClipboardList, label: t('adminNav.orders'), badge: stats.pending_orders, testid: 'nav-orders' },
        { path: '/admin/buyers', icon: UserCheck, label: 'Buyers', testid: 'nav-buyers' },
        { path: '/admin/sourcing-requests', icon: SearchIcon, label: 'Sourcing Requests', testid: 'nav-sourcing-requests' },
        { path: '/admin/leads', icon: Users, label: 'Leads', badge: stats.pending_leads, testid: 'nav-leads' },
        { path: '/admin/prospects', icon: Target, label: 'Prospects', testid: 'nav-prospects' },
        { path: '/admin/contacts', icon: MessageSquare, label: 'Contacts', testid: 'nav-contacts' },
      ],
    },
    {
      title: 'Marketing',
      items: [
        { path: '/admin/campaigns', icon: Send, label: 'Campaigns', testid: 'nav-campaigns' },
        { path: '/admin/email-analytics', icon: BarChart3, label: 'Email Analytics', testid: 'nav-email-analytics' },
        { path: '/admin/sender-profiles', icon: Mail, label: 'Sender Profiles', testid: 'nav-sender-profiles' },
        { path: '/admin/subscribers', icon: Mail, label: 'Subscribers', testid: 'nav-subscribers' },
      ],
    },
    {
      title: 'Sourcing',
      items: [
        { path: '/admin/suppliers', icon: Truck, label: 'Suppliers', testid: 'nav-suppliers' },
        { path: '/admin/rfq-campaigns', icon: ShoppingCart, label: 'RFQ Campaigns', testid: 'nav-rfq-campaigns' },
        { path: '/admin/supplier-quotes', icon: ClipboardCheck, label: 'Supplier Quotes', testid: 'nav-supplier-quotes' },
      ],
    },
    {
      title: 'Admin',
      items: [
        { path: '/admin/analytics', icon: BarChart3, label: 'Analytics', testid: 'nav-analytics' },
        { path: '/admin/settings', icon: Settings, label: t('adminNav.settings'), testid: 'nav-settings' },
      ],
    },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-heavy px-4 py-3 flex items-center gap-3">
        <button onClick={() => setSidebarOpen(true)} className="p-2 text-white shrink-0">
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex-1">
          <AdminSearchBar />
        </div>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-[#0A0A0A] border-r border-white/5 z-50 transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 flex items-center justify-between">
            <Link to="/admin">
              <img src={LOGO_URL} alt="LUXOR TEK" className="h-10" />
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 text-white/60"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-4 overflow-y-auto">
            {navSections.map((section) => (
              <div key={section.title}>
                <div className="px-4 py-1 text-[10px] font-semibold uppercase tracking-widest text-white/30">
                  {section.title}
                </div>
                <div className="space-y-0.5">
                  {section.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-4 py-2.5 transition-colors text-sm ${
                        isActive(item.path, item.exact)
                          ? 'bg-[#00D4FF]/10 text-[#00D4FF] border-l-2 border-[#00D4FF]'
                          : 'text-white/60 hover:text-white hover:bg-white/5'
                      }`}
                      data-testid={item.testid}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="flex-1">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="px-2 py-0.5 text-xs font-bold bg-[#00D4FF] text-black rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* User + Language */}
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-8 h-8 flex items-center justify-center bg-[#00D4FF]/20 text-[#00D4FF] font-bold text-sm">
                {adminInfo?.name?.charAt(0).toUpperCase() || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{adminInfo?.name}</div>
                <div className="text-xs text-white/40 truncate">{adminInfo?.email}</div>
              </div>
            </div>
            {/* Language Toggle */}
            <div className="flex items-center gap-3 px-4 py-2 mt-1" data-testid="admin-language-toggle">
              <Globe className="w-5 h-5 text-white/40" />
              <span className="text-xs text-white/40 uppercase tracking-wider">{t('adminNav.language')}</span>
              <div className="ml-auto flex items-center bg-white/5 border border-white/10 rounded-sm overflow-hidden">
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-2.5 py-1 text-xs font-semibold tracking-wider transition-colors ${
                    language === 'en'
                      ? 'bg-[#00D4FF] text-black'
                      : 'text-white/50 hover:text-white'
                  }`}
                  data-testid="admin-lang-en"
                >
                  EN
                </button>
                <button
                  onClick={() => setLanguage('es')}
                  className={`px-2.5 py-1 text-xs font-semibold tracking-wider transition-colors ${
                    language === 'es'
                      ? 'bg-[#00D4FF] text-black'
                      : 'text-white/50 hover:text-white'
                  }`}
                  data-testid="admin-lang-es"
                >
                  ES
                </button>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full mt-2 flex items-center gap-3 px-4 py-3 text-white/60 hover:text-red-400 hover:bg-red-400/10 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span>{t('adminNav.logout')}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Global Search Bar */}
        <div className="sticky top-0 z-30 bg-[#050505]/95 backdrop-blur-sm border-b border-white/5 px-6 py-3 hidden lg:block">
          <AdminSearchBar />
        </div>
        <Outlet />
      </main>
    </div>
  );
};
